
import React, { useMemo } from 'react';
import { SimulationConfig, MidiNote, MidiNoteAction } from '../../../types';
import { AUDIO } from '../../../services/AudioService';

interface MidiPitchHistogramProps {
    notes: MidiNote[];
    minPitch: number;
    maxPitch: number;
    darkMode: boolean;
    config: SimulationConfig;
}

export const MidiPitchHistogram: React.FC<MidiPitchHistogramProps> = ({ notes, minPitch, maxPitch, darkMode, config }) => {
    const { histogram, counts, backgroundCounts } = useMemo(() => {
        const c = new Array(128).fill(0);
        const bg = new Array(128).fill(0);
        notes.forEach(n => {
            const action = AUDIO.getNoteAction(n, config);
            if (action >= MidiNoteAction.BOUNCE) c[n.midi]++;
            else if (action >= MidiNoteAction.PLAY) bg[n.midi]++;
        });
        const max = Math.max(...c, ...bg, 1);
        return { histogram: c.map((val, i) => (val + bg[i]) / max), counts: c, backgroundCounts: bg };
    }, [notes, config]);

    const activeCount = useMemo(() => {
        let sum = 0;
        for (let i = minPitch; i <= maxPitch; i++) {
            if (i >= 0 && i < 128) sum += counts[i];
        }
        return sum;
    }, [counts, minPitch, maxPitch]);

    const backgroundCount = useMemo(() => {
        let sum = 0;
        for (let i = 0; i < 128; i++) sum += backgroundCounts[i];
        return sum;
    }, [backgroundCounts]);

    return (
        <div className="space-y-1">
            <div className="h-24 w-full flex items-end gap-[1px] bg-black/20 rounded-lg p-2 border border-white/5 overflow-hidden relative group">
                {histogram.map((val, i) => {
                    const isBounce = i >= minPitch && i <= maxPitch && counts[i] > 0;
                    const isBackground = backgroundCounts[i] > 0;
                    
                    let color = 'bg-white/10';
                    if (isBounce) color = 'bg-blue-500';
                    else if (isBackground) color = 'bg-purple-500/50';

                    return (
                        <div 
                            key={i} 
                            className={`flex-1 transition-all duration-300 ${color}`}
                            style={{ height: `${val * 100}%`, opacity: val > 0 ? 1 : 0.1 }}
                        >
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-between items-center px-1">
                <p className="text-[9px] text-white/40 uppercase tracking-tighter">0</p>
                <div className="flex gap-4">
                    <p className="text-[9px] text-blue-400 font-bold uppercase tracking-widest">{activeCount} bounce notes</p>
                    {backgroundCount > 0 && (
                        <p className="text-[9px] text-purple-400 font-bold uppercase tracking-widest">{backgroundCount} background notes</p>
                    )}
                </div>
                <p className="text-[9px] text-white/40 uppercase tracking-tighter">127</p>
            </div>
        </div>
    );
};
